import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import logoImg from '../assets/logo-removebg-preview.png';

const PAGE_SIZE = 21;

const useReveal = (delay = 0) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(() => setVis(true), delay); obs.disconnect(); }
    }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

const Infusions = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tableRef, tableVis] = useReveal(100);

  const rawProducts = t('infusionsPage.products', { returnObjects: true }) || [];
  const allProducts = rawProducts.map((p, i) => ({ id: i + 1, ...p }));

  const filtered = allProducts.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.presentation.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  return (
    <div className="inf-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Playfair+Display:wght@700;800&display=swap');

        @keyframes infHeroIn { from{opacity:0;transform:translateY(32px)} to{opacity:1;transform:translateY(0)} }
        @keyframes infRowIn  { from{opacity:0;transform:translateX(-16px)} to{opacity:1;transform:translateX(0)} }

        .inf-root { font-family:'Outfit',sans-serif; }

        .inf-hero {
          position:relative; min-height:260px; display:flex; align-items:center;
          justify-content:center; overflow:hidden; padding-top:80px;
          background:linear-gradient(135deg,#0d2233 0%,#1a3a4a 55%,#0f2d3d 100%);
        }
        .inf-hero-bg { position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0.15; }
        .inf-hero-grid {
          position:absolute;inset:0;pointer-events:none;
          background-image:
            linear-gradient(rgba(58,143,163,0.07) 1px,transparent 1px),
            linear-gradient(90deg,rgba(58,143,163,0.07) 1px,transparent 1px);
          background-size:56px 56px;
        }
        .inf-hero-line {
          position:absolute;top:0;left:0;right:0;height:3px;
          background:linear-gradient(90deg,transparent,#3a8fa3,#5aab9a,#3a8fa3,transparent);
        }
        .inf-hero-content {
          position:relative;z-index:2;text-align:center;padding:60px 24px 56px;
          animation:infHeroIn 0.85s cubic-bezier(0.4,0,0.2,1) 0.1s both;
        }
        .inf-hero-eyebrow {
          display:inline-block;font-size:0.72rem;font-weight:600;
          letter-spacing:0.14em;text-transform:uppercase;
          color:#5aab9a;background:rgba(90,171,154,0.12);
          border:1px solid rgba(90,171,154,0.3);
          padding:5px 16px;border-radius:999px;margin-bottom:18px;
        }
        .inf-hero-title {
          font-family:'Playfair Display',serif;
          font-size:clamp(2.8rem,7vw,5rem);font-weight:800;
          color:#fff;margin:0 0 12px;line-height:1.1;
          letter-spacing:0.06em;text-transform:uppercase;
          text-decoration:underline;text-underline-offset:8px;
          text-decoration-color:rgba(58,143,163,0.6);
          text-shadow:0 2px 24px rgba(0,0,0,0.3);
        }
        .inf-hero-sub {
          font-size:0.82rem;letter-spacing:0.1em;
          color:rgba(255,255,255,0.38);text-transform:uppercase;
        }

        .inf-body {
          background:linear-gradient(180deg,#e8f5f7 0%,#d4eef2 60%,#e0f4f7 100%);
          padding:60px 0 80px;position:relative;overflow:hidden;min-height:60vh;
        }
        .inf-body::before {
          content:'';position:absolute;inset:0;pointer-events:none;
          background-image:radial-gradient(circle,rgba(58,143,163,0.05) 1px,transparent 1px);
          background-size:36px 36px;
        }
        .inf-blob-tl { position:absolute;width:400px;height:400px;border-radius:50%;background:rgba(58,143,163,0.07);top:-100px;left:-100px;filter:blur(80px);pointer-events:none; }
        .inf-blob-br { position:absolute;width:350px;height:350px;border-radius:50%;background:rgba(90,171,154,0.06);bottom:-80px;right:-80px;filter:blur(80px);pointer-events:none; }
        .inf-inner { max-width:1200px;margin:0 auto;padding:0 24px;position:relative;z-index:1; }

        .inf-card {
          background:rgba(255,255,255,0.90);
          backdrop-filter:blur(20px) saturate(1.4);
          -webkit-backdrop-filter:blur(20px) saturate(1.4);
          border:1px solid rgba(255,255,255,0.95);
          border-radius:20px;
          box-shadow:0 8px 40px rgba(58,143,163,0.10),0 2px 8px rgba(0,0,0,0.05);
          overflow:hidden;
          opacity:0;transform:translateY(24px);
          transition:all 0.75s cubic-bezier(0.4,0,0.2,1);
          position:relative;
        }
        .inf-card.in { opacity:1;transform:translateY(0); }

        /* Search */
        .inf-search-bar { padding:20px 24px 16px;display:flex;justify-content:flex-end; }
        .inf-search-wrap {
          display:inline-flex;align-items:center;gap:10px;
          background:#f0f8fb;border:1.5px solid rgba(58,143,163,0.2);
          border-radius:999px;padding:9px 20px;transition:all 0.3s;min-width:240px;
        }
        .inf-search-wrap:focus-within {
          border-color:#3a8fa3;box-shadow:0 0 0 3px rgba(58,143,163,0.12);background:#fff;
        }
        .inf-search-input {
          border:none;background:transparent;outline:none;
          font-family:'Outfit',sans-serif;font-size:0.88rem;color:#1a3a4a;width:200px;
        }
        .inf-search-input::placeholder { color:#8ab4be; }

        /* Pill list wrap with watermark */
        .inf-list-wrap {
          position:relative;overflow:hidden;
          padding:0 24px 16px;
        }
        .inf-list-watermark {
          position:absolute;top:50%;left:50%;
          transform:translate(-50%,-50%);
          width:55%;height:55%;
          background-size:contain;background-repeat:no-repeat;background-position:center;
          opacity:0.07;pointer-events:none;z-index:0;
          filter:grayscale(0.3) brightness(1.1);
        }

        /* Table header */
        .inf-list-header {
          display:grid;grid-template-columns:52px 1fr auto;
          align-items:center;gap:12px;
          padding:10px 18px;margin-bottom:8px;
          background:linear-gradient(135deg,#1a3a4a,#0d2233);
          border-radius:12px;position:relative;z-index:1;
        }
        .inf-list-header span {
          font-size:0.68rem;font-weight:700;letter-spacing:0.12em;
          text-transform:uppercase;color:rgba(255,255,255,0.75);
        }

        /* Pill rows */
        .inf-pill-list { display:flex;flex-direction:column;gap:8px;position:relative;z-index:1; }
        .inf-pill-row {
          display:grid;grid-template-columns:52px 1fr auto;
          align-items:center;gap:12px;
          background:rgba(255,255,255,0.88);
          border-radius:999px;padding:10px 18px;
          border:1px solid rgba(58,143,163,0.12);
          transition:all 0.25s cubic-bezier(0.4,0,0.2,1);
          animation:infRowIn 0.4s cubic-bezier(0.4,0,0.2,1) both;
        }
        .inf-pill-row:hover {
          background:rgba(255,255,255,0.98);
          border-color:rgba(58,143,163,0.35);
          box-shadow:0 4px 20px rgba(58,143,163,0.14);
          transform:translateX(4px);
        }
        .inf-pill-badge {
          width:34px;height:34px;border-radius:50%;flex-shrink:0;
          background:linear-gradient(135deg,#3a8fa3,#5aab9a);
          display:flex;align-items:center;justify-content:center;
          font-size:0.72rem;font-weight:700;color:#fff;
          box-shadow:0 2px 8px rgba(58,143,163,0.3);
        }
        .inf-pill-name {
          font-size:0.9rem;font-weight:600;color:#1a3a4a;
        }
        .inf-pill-dose {
          font-size:0.78rem;color:#3a8fa3;
          background:rgba(58,143,163,0.08);
          padding:4px 12px;border-radius:999px;
          white-space:nowrap;font-weight:500;
          border:1px solid rgba(58,143,163,0.15);
        }

        /* Empty */
        .inf-empty { text-align:center;padding:56px 24px;color:#8ab4be;font-size:0.95rem; }
        .inf-empty-icon { font-size:2.5rem;margin-bottom:12px; }

        /* Footer */
        .inf-footer {
          display:flex;align-items:center;justify-content:space-between;
          flex-wrap:wrap;gap:12px;padding:16px 24px;
          border-top:1px solid rgba(58,143,163,0.1);
          background:rgba(248,252,254,0.8);
        }
        .inf-count { font-size:0.82rem;color:#5a7d8a; }
        .inf-count strong { color:#1a3a4a;font-weight:700; }
        .inf-pagination { display:flex;align-items:center;gap:6px; }
        .inf-page-btn {
          font-family:'Outfit',sans-serif;font-size:0.82rem;font-weight:500;
          padding:7px 16px;border-radius:8px;
          border:1.5px solid rgba(58,143,163,0.25);
          background:rgba(255,255,255,0.8);color:#3a8fa3;cursor:pointer;
          transition:all 0.25s cubic-bezier(0.4,0,0.2,1);
        }
        .inf-page-btn:hover:not(:disabled) {
          background:#3a8fa3;color:#fff;border-color:#3a8fa3;
          transform:translateY(-1px);box-shadow:0 4px 14px rgba(58,143,163,0.3);
        }
        .inf-page-btn:disabled { opacity:0.35;cursor:not-allowed; }
        .inf-page-num {
          font-family:'Outfit',sans-serif;font-size:0.82rem;font-weight:700;
          padding:7px 14px;border-radius:8px;
          background:linear-gradient(135deg,#3a8fa3,#5aab9a);
          color:#fff;border:none;box-shadow:0 2px 8px rgba(58,143,163,0.3);
        }
      `}</style>

      <section className="inf-hero">
        <div className="inf-hero-grid" />
        <div className="inf-hero-line" />
        <div className="inf-hero-content">
          <div className="inf-hero-eyebrow">{t('infusionsPage.hero.eyebrow')}</div>
          <h1 className="inf-hero-title">{t('infusionsPage.hero.title')}</h1>
          <div className="inf-hero-sub">{t('infusionsPage.hero.subtitle')}</div>
        </div>
      </section>

      <section className="inf-body">
        <div className="inf-blob-tl" /><div className="inf-blob-br" />
        <div className="inf-inner">
          <div ref={tableRef} className={`inf-card ${tableVis ? 'in' : ''}`}>

            {/* Search */}
            <div className="inf-search-bar">
              <div className="inf-search-wrap">
                <input
                  className="inf-search-input"
                  type="text"
                  placeholder={t('infusionsPage.search.placeholder')}
                  value={search}
                  onChange={handleSearch}
                />
              </div>
            </div>

            {/* Pill List */}
            <div className="inf-list-wrap">
              <div className="inf-list-watermark" style={{backgroundImage:`url(${logoImg})`}} />

              {/* Header row */}
              <div className="inf-list-header">
                <span>{t('infusionsPage.table.number')}</span>
                <span>{t('infusionsPage.table.productName')}</span>
                <span>{t('infusionsPage.table.presentation')}</span>
              </div>

              {paginated.length === 0 ? (
                <div className="inf-empty">
                  <div className="inf-empty-icon">🔍</div>
                  {t('infusionsPage.empty')} "<strong>{search}</strong>"
                </div>
              ) : (
                <div className="inf-pill-list">
                  {paginated.map((product, i) => (
                    <div
                      key={product.id}
                      className="inf-pill-row"
                      style={{ animationDelay: `${i * 30}ms` }}
                    >
                      <div className="inf-pill-badge">{product.id}</div>
                      <div className="inf-pill-name">{product.name}</div>
                      <div className="inf-pill-dose">{product.presentation}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="inf-footer">
              <div className="inf-count">
                {t('infusionsPage.pagination.showing')}{' '}
                <strong>{Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(page * PAGE_SIZE, filtered.length)}</strong>{' '}
                {t('infusionsPage.pagination.of')} <strong>{filtered.length}</strong>
              </div>
              <div className="inf-pagination">
                <button className="inf-page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                  {t('infusionsPage.pagination.prev')}
                </button>
                <span className="inf-page-num">{page}</span>
                <button className="inf-page-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
                  {t('infusionsPage.pagination.next')}
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default Infusions;